import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Grid, TextField, Select, MenuItem, FormControl, Button, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert
} from '@mui/material';

const PaceCalculator = () => {
  const [time, setTime] = useState({ h: null, m: null, s: null });
  const [distance, setDistance] = useState(null);
  const [distanceUnit, setDistanceUnit] = useState('miles');
  const [pace, setPace] = useState({ m: null, s: null });
  const [paceUnit, setPaceUnit] = useState('mile');
  const [splits, setSplits] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const toSeconds = (h, m, s) => (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
  const formatTime = (totalSeconds, showHours = true) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) return '';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.round(totalSeconds % 60);
    const pad = (num) => num.toString().padStart(2, '0');
    if (showHours && h > 0) return `${h}:${pad(m)}:${pad(s)}`;
    return `${m}:${pad(s)}`;
  };

  const toMiles = (dist, unit) => {
    if (dist === null || isNaN(dist)) return 0;
    switch (unit) {
      case 'kilometers': return dist * 0.621371;
      case 'meters': return dist * 0.000621371;
      case 'yards': return dist * 0.000568182;
      default: return dist;
    }
  };

  const generateSplits = (totalDist, unit, pacePerUnit) => {
    const numSplits = Math.floor(totalDist);
    const newSplits = [];
    for (let i = 1; i <= numSplits; i++) {
      newSplits.push({ split: i, time: formatTime(i * pacePerUnit, true) });
    }
    if (totalDist > numSplits) {
      newSplits.push({ split: totalDist.toFixed(2), time: formatTime(totalDist * pacePerUnit, true) });
    }
    setSplits(newSplits);
  };

  const handleCalculate = () => {
    setError('');
    const currentH = time.h === null ? 0 : time.h;
    const currentM = time.m === null ? 0 : time.m;
    const currentS = time.s === null ? 0 : time.s;
    const currentDistance = distance === null ? 0 : parseFloat(distance);
    const currentPaceM = pace.m === null ? 0 : pace.m;
    const currentPaceS = pace.s === null ? 0 : pace.s;

    const timeProvided = toSeconds(currentH, currentM, currentS) > 0;
    const distProvided = currentDistance > 0;
    const paceProvided = toSeconds(0, currentPaceM, currentPaceS) > 0;

    const numProvided = [timeProvided, distProvided, paceProvided].filter(Boolean).length;

    if (numProvided < 2) {
      setError('Please fill in at least two fields (Time, Distance, or Pace) to calculate the third.');
      setSplits([]);
      return;
    }

    const totalTimeSec = toSeconds(currentH, currentM, currentS);
    const totalDistMiles = toMiles(currentDistance, distanceUnit);
    const totalPaceSec = toSeconds(0, currentPaceM, currentPaceS);

    if (timeProvided && distProvided) {
      const pacePerMile = totalTimeSec / totalDistMiles;
      const paceInTargetUnit = paceUnit === 'kilometer' ? pacePerMile / 1.60934 : pacePerMile;
      const { m, s } = formatTime(paceInTargetUnit, false).split(':').map(Number);
      setPace({ m: m || null, s: s || null });
      generateSplits(currentDistance, distanceUnit, paceUnit === distanceUnit ? paceInTargetUnit : (distanceUnit === 'miles' ? pacePerMile : pacePerMile / 1.60934));
    } else if (distProvided && paceProvided) {
      const pacePerMile = paceUnit === 'kilometer' ? totalPaceSec * 1.60934 : totalPaceSec;
      const totalSeconds = pacePerMile * totalDistMiles;
      const formattedTime = formatTime(totalSeconds, true);
      const parts = formattedTime.split(':').map(Number);
      if (totalSeconds >= 3600) {
        setTime({ h: parts[0] || null, m: parts[1] || null, s: parts[2] || null });
      } else {
        setTime({ h: null, m: parts[0] || null, s: parts[1] || null });
      }
      generateSplits(currentDistance, distanceUnit, pacePerMile / (distanceUnit === 'miles' ? 1 : 1.60934));
    } else if (timeProvided && paceProvided) {
      const pacePerMile = paceUnit === 'kilometer' ? totalPaceSec * 1.60934 : totalPaceSec;
      const distMiles = totalTimeSec / pacePerMile;
      setDistance(parseFloat(distMiles.toFixed(2)));
      setDistanceUnit('miles');
      generateSplits(distMiles, 'miles', pacePerMile);
    }
  };

  const handleClear = () => {
    setTime({ h: null, m: null, s: null });
    setDistance(null);
    setDistanceUnit('miles');
    setPace({ m: null, s: null });
    setPaceUnit('mile');
    setSplits([]);
    setError('');
  };

  const setRaceDistance = (dist, unit) => {
    setDistance(dist);
    setDistanceUnit(unit);
  };

  const handlePrintSplits = () => {
    if (splits.length === 0) {
      alert('No splits to print. Please calculate splits first.');
      return;
    }

    const printWindow = window.open('', '_blank', 'height=600,width=400');
    if (!printWindow) {
      alert('Please allow pop-ups for this website to print splits.');
      return;
    }

    const displayUnitText = distanceUnit === 'miles' ? 'Mile' : 'KM';

    let splitsHtml = `
      <div style="
        border: 2px solid black;
        padding: 10mm;
        box-sizing: border-box;
        font-family: monospace;
        background-color: white;
        width: 180mm; /* Fixed width for wristband */
        margin: 5mm auto; /* Center horizontally */
      ">
        <p style="font-size: 8pt; text-align: center; margin-bottom: 5mm; color: #555;">
          You can now print this page (Ctrl+P or Cmd+P) or save it as a PDF.
        </p>
        <h4 style="font-size: 12pt; font-weight: bold; text-align: center; margin-bottom: 5mm;">
          Race Splits
        </h4>
        <table style="
          width: 100%;
          font-size: 9pt;
          border-collapse: collapse;
          border: 1px solid black;
        ">
          <thead>
            <tr>
              <th style="padding: 3px 5px; border: 1px solid black; text-align: left; font-weight: bold; background-color: #f0f0f0;">Split</th>
              <th style="padding: 3px 5px; border: 1px solid black; text-align: right; font-weight: bold; background-color: #f0f0f0;">Time</th>
            </tr>
          </thead>
          <tbody>
            ${splits.map(split => `
              <tr>
                <td style="padding: 3px 5px; border: 1px solid black; text-align: left; white-space: nowrap;">${split.split} ${displayUnitText}</td>
                <td style="padding: 3px 5px; border: 1px solid black; text-align: right; white-space: nowrap;">${split.time}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Race Splits</title>
        <style>
          @page {
            size: auto;
            margin: 5mm;
          }
          body {
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
          }
          /* Ensure only the content within the div is visible */
          body > *:not(div) {
            display: none !important;
          }
        </style>
      </head>
      <body>
        ${splitsHtml}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500); // Give browser time to render
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const raceChips = [
    { label: '5K', dist: 5, unit: 'kilometers' }, { label: '10K', dist: 10, unit: 'kilometers' },
    { label: 'Half Marathon', dist: 13.1, unit: 'miles' }, { label: 'Marathon', dist: 26.2, unit: 'miles' }
  ];

  const displayUnit = distanceUnit === 'miles' ? 'Mile' : 'KM';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>Running Pace Calculator</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>Calculate pace, time, or distance, and generate printable splits for your race.</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 4 }}>
          {raceChips.map(race => <Chip key={race.label} label={race.label} onClick={() => setRaceDistance(race.dist, race.unit)} />)}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} sm={4}><Typography variant="h6">Time</Typography><Grid container spacing={1}><Grid item xs={4}><TextField fullWidth label="HH" type="number" value={time.h === null ? '' : time.h} onChange={e => setTime({ ...time, h: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value)) })} inputProps={{ min: 0 }} /></Grid><Grid item xs={4}><TextField fullWidth label="MM" type="number" value={time.m === null ? '' : time.m} onChange={e => setTime({ ...time, m: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value)) })} inputProps={{ min: 0 }} /></Grid><Grid item xs={4}><TextField fullWidth label="SS" type="number" value={time.s === null ? '' : time.s} onChange={e => setTime({ ...time, s: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value)) })} inputProps={{ min: 0 }} /></Grid></Grid></Grid>
          <Grid item xs={12} sm={4}><Typography variant="h6">Distance</Typography><Grid container spacing={1}><Grid item xs={8}><TextField fullWidth type="number" value={distance === null ? '' : distance} onChange={e => setDistance(e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value)))} inputProps={{ min: 0 }} /></Grid><Grid item xs={4}><FormControl fullWidth><Select value={distanceUnit} onChange={e => setDistanceUnit(e.target.value)}><MenuItem value="miles">Miles</MenuItem><MenuItem value="kilometers">KM</MenuItem><MenuItem value="meters">Meters</MenuItem><MenuItem value="yards">Yards</MenuItem></Select></FormControl></Grid></Grid></Grid>
          <Grid item xs={12} sm={4}><Typography variant="h6">Pace</Typography><Grid container spacing={1}><Grid item xs={4}><TextField fullWidth label="MM" type="number" value={pace.m === null ? '' : pace.m} onChange={e => setPace({ ...pace, m: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value)) })} inputProps={{ min: 0 }} /></Grid><Grid item xs={4}><TextField fullWidth label="SS" type="number" value={pace.s === null ? '' : pace.s} onChange={e => setPace({ ...pace, s: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value)) })} inputProps={{ min: 0 }} /></Grid><Grid item xs={4}><FormControl fullWidth><Select value={paceUnit} onChange={e => setPaceUnit(e.target.value)}><MenuItem value="mile">per Mile</MenuItem><MenuItem value="kilometer">per KM</MenuItem></Select></FormControl></Grid></Grid></Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="contained" color="primary" size="large" onClick={handleCalculate}>Calculate</Button>
          <Button variant="outlined" size="large" onClick={handleClear}>Clear</Button>
        </Box>
      </Paper>

      {splits.length > 0 && (
        <Paper sx={{ mt: 4, p: { xs: 2, md: 4 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>Race Splits</Typography>
            <Button variant="contained" onClick={handlePrintSplits}>Print Splits</Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Split</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {splits.map((split) => (
                  <TableRow key={split.split}>
                    <TableCell>{split.split} {displayUnit}</TableCell>
                    <TableCell align="right">{split.time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="contained" onClick={handlePrintSplits}>Print Splits</Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default PaceCalculator;